import React from "react";
import { Card, CardActionArea, CardContent, Typography } from "@mui/material";
import axios from "../../utils/axios.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

interface MemoryCardProps {
  id: string;
  memory: string;
  onMemoryUpdated: () => void;
  onMemorySaved: () => void;
}

const MemoryCard: React.FC<MemoryCardProps> = ({
  id,
  memory,
  onMemoryUpdated,
  onMemorySaved,
}) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [displayText, setDisplayText] = React.useState(memory);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleEditSubmit = async (newMemoryText: string) => {
    onMemorySaved();
    await axios.patch(
      `/v1/memory/${id}/`,
      JSON.stringify({ memory: newMemoryText }),
    );
    setDisplayText(newMemoryText);
    onMemoryUpdated();
    handleCloseDialog();
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          height: 150,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "#f5f5f0",
          transition: "box-shadow 0.2s ease-in-out",
          "&:hover": {
            boxShadow: 2,
          },
        }}
      >
        <CardActionArea
          onClick={handleOpenDialog}
          sx={{ height: "100%", alignItems: "stretch", p: 2 }}
        >
          <CardContent sx={{ p: "0 !important" }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 5,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {displayText}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>

      <TallyDialog
        open={isDialogOpen}
        title={i18n._(
          msg({
            context: "Title of a dialog enabling the user to edit a memory.",
            message: "Edit memory",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Subtitle of a dialog explaining how the application uses the memory to improve responses in the future.",
            message:
              "By creating a memory, I will learn the information so I can use it going forward in our conversations",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title of the edit memory dialog.",
            message: "Save",
          }),
        )}
        onSubmit={handleEditSubmit}
        initialInputValue={displayText}
        handleDialogClose={handleCloseDialog}
      />
    </>
  );
};

export default MemoryCard;
